import filter from 'lodash/filter';
import find from 'lodash/find';
import flatMap from 'lodash/flatMap';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import toPairs from 'lodash/toPairs';
import {
  AddressDTO,
  InteractiveFormFieldValueDTO,
} from 'dtos';
import {
  FieldValueType,
} from 'dtos/fieldValueType';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  getAddressString,
} from 'utils/string';
import {
  FillableFormField,
  FormDynamicField,
  FormFieldValue,
  FormFieldsData,
} from 'views/EMR/components/FillableForm/types';

export function isFormDynamicField(field: FormFieldValue): field is FormDynamicField {
  return typeof field === 'object' && field !== null && 'type' in field;
}

export function normalizeFormData(
  data: FormFieldsData,
  fields: FillableFormField[],
): FormFieldsData {
  const updated = { ...data };
  fields.forEach((field) => {
    if (field?.type === 'usAddress') {
      const raw = data[field.id ?? ''];
      const parts = (isFormDynamicField(raw)
        && typeof raw.value === 'object'
        && !isNil(raw.value))
        ? raw.value
        : undefined;
      const composed = getAddressString(parts as AddressDTO);
      const prev = updated[field.id ?? ''];
      if (isFormDynamicField(prev)) {
        updated[field.id ?? ''] = { ...prev, value: composed };
      }
    }
  });
  return updated;
}

export function normalizeFieldValues(
  data: FormFieldsData,
): InteractiveFormFieldValueDTO[] {
  return flatMap(
    filter(toPairs(data), ([, value]) => isObject(value) && 'type' in value),
    ([, field]) => {
      const typedField = field as FillableFormField;
      const formId = typedField.formId ?? 0;

      if (typedField?.type === 'checkboxGroup' && isArray(typedField.value)) {
        return typedField.value.map((childId) => ({
          formId,
          fieldId: childId.toString(),
          value: '✔',
          type: FieldValueType.Text,
        }));
      }

      if (typedField?.type === 'radioGroup' && isArray(typedField.children) && typedField.value) {
        const match = find(typedField.children, { id: typedField.value as string });
        return [{
          formId,
          fieldId: match?.id?.toString() ?? '',
          value: '✔',
          type: FieldValueType.Text,
        }];
      }

      if (typedField?.type === 'checkbox') {
        return [{
          formId,
          fieldId: typedField.id?.toString() ?? '',
          value: typedField.value === true ? '✔' : '',
          type: FieldValueType.Text,
        }];
      }
      if (typedField?.type === 'dollar' && !isEmptyString(typedField?.value)) {
        return [{
          formId,
          fieldId: typedField.id?.toString() ?? '',
          value: `$${typedField?.value}`,
          type: FieldValueType.Text,
        }];
      }
      if (typedField?.type === 'percent' && !isEmptyString(typedField?.value)) {
        return [{
          formId,
          fieldId: typedField.id?.toString() ?? '',
          value: `${typedField?.value}%`,
          type: FieldValueType.Text,
        }];
      }

      const rawValue = typedField?.value?.toString() ?? '';
      const isSignatureField = typedField?.type === 'signature' || typedField?.type === 'initial';
      const isImage = isSignatureField && /^data:image\//i.test(rawValue);

      return [{
        formId,
        fieldId: typedField.id?.toString() ?? '',
        value: rawValue,
        type: isImage ? FieldValueType.ImageDataURI : FieldValueType.Text,
      }];
    },
  );
}
